import { useEffect, useState } from "react";
import { ScanLine, Layers, FileText, ArrowRight, ArrowLeft, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "strato_onboarded_v1";

const SLIDES = [
  {
    n: 1,
    bubble: "#A8D5BA", // mint
    blob: "blob-mint",
    blob2: "blob-lavender",
    icon: ScanLine,
    title: "Cuéntame el contexto",
    desc: "Servicio, cliente, presupuesto. En 30 segundos. Cuanto más concreto seas, mejor te entiendo.",
    accent: "scribble-mint",
  },
  {
    n: 2,
    bubble: "#F4B4C4", // rose
    blob: "blob-rose",
    blob2: "blob-peach",
    icon: Layers,
    title: "Yo construyo la oferta",
    desc: "4 packs con precios reales anclados a tu presupuesto. Riesgos, upsells y respuesta a rebaja incluidos.",
    accent: "scribble-coral",
  },
  {
    n: 3,
    bubble: "#C9C7F7", // lavender
    blob: "blob-lavender",
    blob2: "blob-sky",
    icon: FileText,
    title: "Tú la envías",
    desc: "Mensaje listo para copiar y enviar. Sin pensar la primera frase. Sin regalar trabajo.",
    accent: "scribble-lavender",
  },
];

export default function OnboardingModal() {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    } catch {
      // localStorage no disponible — no mostrar para no molestar
    }
  }, []);

  const finish = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {
      // ignora si localStorage está bloqueado
    }
    setOpen(false);
  };

  if (!open) return null;
  const slide = SLIDES[idx];
  const Icon = slide.icon;
  const isLast = idx === SLIDES.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="onboarding-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-lg bg-background rounded-3xl border border-black/[0.06] shadow-[0_30px_80px_rgba(0,0,0,0.25)] overflow-hidden">
        {/* Skip */}
        <button
          onClick={finish}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white border border-black/[0.06] grid place-items-center text-foreground/60 hover:text-foreground transition"
          data-testid="onboarding-skip"
          aria-label="Saltar onboarding"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Blobs decorativos */}
        <div className={`blob ${slide.blob} w-[320px] h-[320px] -top-16 -left-16`} />
        <div className={`blob ${slide.blob2} w-[260px] h-[260px] -bottom-16 -right-10`} style={{ animationDelay: "2s" }} />

        {/* Contenido */}
        <div className="relative px-8 pt-12 pb-8 text-center">
          <span className="pill mb-6 mx-auto inline-flex" data-testid="onboarding-pill">
            <Sparkles className="w-3 h-3" /> Bienvenido a Strato
          </span>

          <div className="step-bubble mx-auto" style={{ background: slide.bubble }}>
            {slide.n}
          </div>

          <div className="mt-7 flex items-center justify-center gap-2">
            <Icon className="w-4 h-4 text-foreground/60" strokeWidth={2} />
            <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-foreground/55">
              Paso {slide.n} de {SLIDES.length}
            </span>
          </div>

          <h2
            className="mt-3 font-display text-3xl md:text-4xl font-black tracking-[-0.03em] leading-[1.05]"
            data-testid="onboarding-title"
          >
            <span className={`scribble-underline ${slide.accent}`}>{slide.title}</span>
          </h2>

          <p className="mt-5 text-foreground/65 leading-relaxed text-[15px] max-w-md mx-auto">
            {slide.desc}
          </p>

          {/* Dots */}
          <div className="mt-9 flex items-center justify-center gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.n}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-foreground" : "w-1.5 bg-foreground/20 hover:bg-foreground/40"}`}
                aria-label={`Ir al paso ${i + 1}`}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="mt-7 flex items-center justify-between gap-3">
            <button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="btn-apple-outline text-sm disabled:opacity-0 disabled:pointer-events-none"
              data-testid="onboarding-back"
            >
              <ArrowLeft className="w-4 h-4" /> Atrás
            </button>

            {isLast ? (
              <button
                onClick={finish}
                className="btn-apple text-sm"
                data-testid="onboarding-finish"
              >
                Empezar <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => setIdx((i) => Math.min(SLIDES.length - 1, i + 1))}
                className="btn-apple text-sm"
                data-testid="onboarding-next"
              >
                Siguiente <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
